import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: same as above
import { useOrgContext } from '@/hooks/useOrgContext';
import { usePolicyFilters } from '@/hooks/usePolicyFilters';
import { usePolicySelection } from '@/hooks/usePolicySelection';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarClock, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PolicyTableHeader } from '@/components/insurance/PolicyTableHeader';
import { PolicyTableRow } from '@/components/insurance/PolicyTableRow';
import { PolicyTableSkeleton } from '@/components/insurance/PolicyTableSkeleton';
import { DeletePoliciesDialog } from '@/components/insurance/DeletePoliciesDialog';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { RENEWAL_COLUMNS } from '@/lib/tableColumns';
import type {
  IInsurancePolicy,
  IOrgInsuranceType,
  IRenewalTracking,
  TInvoiceStatus,
  TRenewalStatus,
} from '@/@types/insurance';

function renewalAccessor(policy: IInsurancePolicy, columnId: string): string | number {
  switch (columnId) {
    case 'policyNumber':
      return policy.policyNumber;
    case 'customer':
      return policy.customerName ?? '';
    case 'provider':
      return policy.providerName ?? '';
    case 'type':
      return policy.insuranceTypeName ?? '';
    case 'status':
      return policy.status;
    case 'premium':
      return policy.premium ?? 0;
    case 'invoice':
      return policy.invoiceStatus ?? 'not_paid';
    case 'createdBy':
      return policy.creatorName ?? '';
    case 'issued':
      return policy.issuedAt ?? policy.createdAt;
    case 'expires':
      return policy.expiresAt ?? 0;
    default:
      return '';
  }
}

function toPolicyList(
  raw:
    | {
        _id: IInsurancePolicy['_id'];
        orgId: IInsurancePolicy['orgId'];
        insuranceTypeId: IInsurancePolicy['insuranceTypeId'];
        policyNumber: string;
        customerName?: string;
        createdBy: IInsurancePolicy['createdBy'];
        creatorName?: string;
        status: IInsurancePolicy['status'];
        insuranceTypeName?: string;
        issuedAt?: number;
        expiresAt?: number;
        invoiceStatus?: TInvoiceStatus;
        providerName?: string;
        createdAt: number;
        updatedAt: number;
      }[]
    | undefined
): IInsurancePolicy[] {
  if (!raw) return [];
  return raw.map(p => ({ ...p, fieldValues: [] }));
}

export function RenewalListPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const [isSending, setIsSending] = useState(false);

  const columnVis = useColumnVisibility('insurance-renewal', RENEWAL_COLUMNS);

  const updateInvoiceStatus = useMutation(api.insurance.policies.updateInvoiceStatus);
  const triggerManualSend = useMutation(api.insurance.renewalCron.triggerManualSend);
  const setRenewalStatus = useMutation(api.insurance.renewalTracking.setRenewalStatus);

  const handleInvoiceStatusChange = useCallback(
    (policyId: IInsurancePolicy['_id'], status: TInvoiceStatus) => {
      void updateInvoiceStatus({ policyId, invoiceStatus: status });
    },
    [updateInvoiceStatus]
  );

  const handleRenewalStatusChange = useCallback(
    (policyId: string, status: TRenewalStatus) => {
      void setRenewalStatus({
        policyId: policyId as Id<'insurancePolicies'>, // AS_JUSTIFIED: policyId from IInsurancePolicy._id
        renewalStatus: status,
      });
    },
    [setRenewalStatus]
  );

  const rawPolicies = useQuery(
    api.insurance.policies.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const orgTypes = useQuery(
    api.insurance.types.listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const renewalDays = useQuery(
    api.insurance.renewalSettings.getRenewalDays,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const trackingData = useQuery(
    api.insurance.renewalTracking.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const notificationMode = useQuery(
    api.insurance.renewalSettings.getNotificationMode,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const isLoading = orgLoading || rawPolicies === undefined || renewalDays === undefined;
  const allPolicies = toPolicyList(rawPolicies ?? undefined);
  const typeList: IOrgInsuranceType[] = orgTypes ?? [];
  const days = renewalDays ?? 30;

  const trackingByPolicy = useMemo(() => {
    const map = new Map<string, IRenewalTracking>();
    if (trackingData) {
      for (const t of trackingData) {
        map.set(t.policyId, t as IRenewalTracking); // AS_JUSTIFIED: query returns matching shape
      }
    }
    return map;
  }, [trackingData]);

  // Filter to active policies expiring within N days
  const renewalPolicies = useMemo(() => {
    const now = Date.now();
    const cutoff = now + days * 86_400_000;
    return allPolicies.filter(
      p =>
        p.status === 'active' && p.expiresAt != null && p.expiresAt >= now && p.expiresAt <= cutoff
    );
  }, [allPolicies, days]);

  const { filters, filteredPolicies, dispatchFilter } = usePolicyFilters(renewalPolicies);

  const {
    sortState,
    toggleSort,
    sortedItems: sortedPolicies,
  } = useTableSort(
    filteredPolicies,
    RENEWAL_COLUMNS,
    { defaultSort: { sortBy: 'expires', direction: 'asc' } },
    renewalAccessor
  );

  const displayedPolicies = useMemo(() => {
    if (filters.renewalFilter === 'all') return sortedPolicies;
    return sortedPolicies.filter(p => {
      const tracking = trackingByPolicy.get(p._id);
      return tracking?.renewalStatus === filters.renewalFilter;
    });
  }, [sortedPolicies, filters.renewalFilter, trackingByPolicy]);

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
  } = usePolicySelection(displayedPolicies);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <CalendarClock className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to view policy renewals.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary pt-0 pb-8 px-10">
      <div className="sticky top-0 z-20 bg-background-secondary/95 backdrop-blur-sm py-6 -mx-10 px-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Renewal</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isLoading
                ? 'Loading...'
                : `${displayedPolicies.length} ${displayedPolicies.length === 1 ? 'policy' : 'policies'} expiring within ${days} days`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
                <Trash2 aria-hidden="true" className="w-4 h-4 mr-2" />
                Delete ({selectedIds.size})
              </Button>
            )}
            {notificationMode === 'manual' && currentOrg && (
              <Button
                disabled={isSending || displayedPolicies.length === 0}
                onClick={async () => {
                  setIsSending(true);
                  try {
                    await triggerManualSend({ orgId: currentOrg._id });
                    toast.success('Renewal notifications are being sent');
                  } catch {
                    toast.error('Failed to trigger notifications');
                  } finally {
                    setIsSending(false);
                  }
                }}
              >
                <Send aria-hidden="true" className="w-4 h-4 mr-2" />
                {isSending ? 'Sending...' : 'Send Notifications'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="renewal-search" className="sr-only">
            Search policies
          </label>

          <Input
            id="renewal-search"
            placeholder="Search by policy # or customer..."
            value={filters.searchQuery}
            onChange={e => dispatchFilter({ type: 'SET_SEARCH', query: e.target.value })}
            className="max-w-xs"
          />
          <label htmlFor="renewal-type-filter" className="sr-only">
            Filter by type
          </label>
          <Select
            value={filters.typeFilter}
            onValueChange={val => dispatchFilter({ type: 'SET_TYPE_FILTER', typeFilter: val })}
          >
            <SelectTrigger id="renewal-type-filter" className="w-[180px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeList.map(t => (
                <SelectItem key={t._id} value={t._id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label htmlFor="renewal-invoice-filter" className="sr-only">
            Filter by invoice
          </label>
          <Select
            value={filters.invoiceFilter}
            onValueChange={val =>
              dispatchFilter({ type: 'SET_INVOICE_FILTER', invoiceFilter: val })
            }
          >
            <SelectTrigger id="renewal-invoice-filter" className="w-[180px]">
              <SelectValue placeholder="All invoices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="not_paid">Not paid</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <label htmlFor="renewal-status-filter" className="sr-only">
            Filter by renewal status
          </label>
          <Select
            value={filters.renewalFilter}
            onValueChange={val =>
              dispatchFilter({ type: 'SET_RENEWAL_FILTER', renewalFilter: val })
            }
          >
            <SelectTrigger id="renewal-status-filter" className="w-[180px]">
              <SelectValue placeholder="All renewal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All renewal</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
              <SelectItem value="no_need">No need</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={columnVis.allColumns}
              isColumnVisible={columnVis.isColumnVisible}
              toggleColumn={columnVis.toggleColumn}
              resetToDefaults={columnVis.resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <PolicyTableHeader
            sortState={sortState}
            onToggleSort={toggleSort}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            onSelectAllChange={handleSelectAll}
            visibleColumns={columnVis.visibleColumns}
          />
          {isLoading && <PolicyTableSkeleton />}
          {!isLoading &&
            displayedPolicies.map(policy => {
              const tracking = trackingByPolicy.get(policy._id);
              return (
                <PolicyTableRow
                  key={policy._id}
                  policy={policy}
                  isSelected={selectedIds.has(policy._id)}
                  onSelectChange={selected => handleSelectRow(policy._id, selected)}
                  onInvoiceStatusChange={status => handleInvoiceStatusChange(policy._id, status)}
                  renewalTracking={tracking ?? null}
                  onRenewalStatusChange={status => handleRenewalStatusChange(policy._id, status)}
                  visibleColumns={columnVis.visibleColumns}
                  isColumnVisible={columnVis.isColumnVisible}
                />
              );
            })}
          {!isLoading && displayedPolicies.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CalendarClock className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No upcoming renewals</h3>
              <p className="text-sm text-muted-foreground">
                {filters.searchQuery ||
                filters.typeFilter !== 'all' ||
                filters.invoiceFilter !== 'all' ||
                filters.renewalFilter !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : `No active policies are expiring within the next ${days} days`}
              </p>
            </div>
          )}
        </div>
      </div>

      <DeletePoliciesDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedIds.size}
      />
    </div>
  );
}
